import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { Router } from '@angular/router';
import { LoginService } from 'src/app/auth/login.service';
import { SseService } from 'src/app/core/services/sse.service';
import { RegisterRequest, RegisterService } from 'src/app/auth/register.service';

type AuthTab = 'login' | 'register';

@Component({
  selector: 'app-auth-dialog',
  templateUrl: './auth-dialog.component.html',
  styleUrls: ['./auth-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthDialogComponent implements OnDestroy {
  @Output() loggedIn = new EventEmitter<void>();
  @Output() registerSuccess = new EventEmitter<boolean>();
  isVisible = false;
  submitting = false;
  activeTab: AuthTab = 'login';
  errorMessage = '';
  successMessage = '';
  showLoginPassword = false;

  readonly registerPasswordPolicy = {
    minLength: 8,
    maxLength: 20,
  };

  registerPasswordValid = {
    length: false,
    uppercase: false,
    number: false,
    special: false,
  };

  loginForm = {
    userName: '',
    password: '',
  };

  registerForm = {
    userCode: '',
    userName: '',
    phoneNumber: '',
    birthDay: '',
    password: '',
    confirmPassword: '',
    remark: '',
    bankName: '',
    bankAccountNumber: '',
    bankAccountName: '',
    bankBranch: '',
  };

  private resetRegisterForm(): void {
    this.registerForm = {
      userCode: '',
      userName: '',
      phoneNumber: '',
      birthDay: '',
      password: '',
      confirmPassword: '',
      remark: '',
      bankName: '',
      bankAccountNumber: '',
      bankAccountName: '',
      bankBranch: '',
    };
    this.resetRegisterPasswordValidation();
  }

  private resetLoginForm(): void {
    this.loginForm = {
      userName: '',
      password: '',
    };
    this.showLoginPassword = false;
  }

  toggleLoginPasswordVisibility(): void {
    this.showLoginPassword = !this.showLoginPassword;
  }

  constructor(
    private loginService: LoginService,
    private registerService: RegisterService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private sseService: SseService
  ) {}

  open(tab: AuthTab = 'login'): void {
    this.activeTab = tab;
    this.errorMessage = '';
    this.successMessage = '';
    this.isVisible = true;
    if (tab === 'register') {
      this.resetRegisterForm();
    } else {
      this.resetLoginForm();
    }
    document.body.classList.add('overflow-hidden');
    this.cdr.markForCheck();
  }

  close(): void {
    if (!this.isVisible) return;
    this.isVisible = false;
    this.submitting = false;
    this.errorMessage = '';
    this.successMessage = '';
    document.body.classList.remove('overflow-hidden');
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('overflow-hidden');
  }

  setTab(tab: AuthTab): void {
    if (this.submitting) return;
    this.activeTab = tab;
    this.errorMessage = '';
    this.successMessage = '';
    if (tab === 'register') {
      this.resetRegisterForm();
    } else {
      this.resetLoginForm();
    }
    this.cdr.markForCheck();
  }

  canLogin(): boolean {
    return !!this.loginForm.userName.trim() && !!this.loginForm.password && !this.submitting;
  }

  canRegister(): boolean {
    return (
      !!this.registerForm.userCode.trim() &&
      !!this.registerForm.userName.trim() &&
      !!this.registerForm.password &&
      !!this.registerForm.confirmPassword &&
      this.isRegisterPasswordStrong() &&
      this.registerForm.password === this.registerForm.confirmPassword &&
      !this.submitting
    );
  }

  onRegisterPasswordInput(): void {
    const password = this.registerForm.password || '';
    const trimmed = password.slice(0, this.registerPasswordPolicy.maxLength);
    if (trimmed !== password) {
      this.registerForm.password = trimmed;
    }

    this.registerPasswordValid.length =
      trimmed.length >= this.registerPasswordPolicy.minLength && trimmed.length <= this.registerPasswordPolicy.maxLength;
    this.registerPasswordValid.uppercase = /[A-Z]/.test(trimmed);
    this.registerPasswordValid.number = /\d/.test(trimmed);
    this.registerPasswordValid.special = /[^A-Za-z0-9]/.test(trimmed);

    this.cdr.markForCheck();
  }

  isRegisterPasswordStrong(): boolean {
    return (
      this.registerPasswordValid.length &&
      this.registerPasswordValid.uppercase &&
      this.registerPasswordValid.number &&
      this.registerPasswordValid.special
    );
  }

  private resetRegisterPasswordValidation(): void {
    this.registerPasswordValid = {
      length: false,
      uppercase: false,
      number: false,
      special: false,
    };
  }

  submitLogin(): void {
    if (!this.canLogin()) return;

    this.submitting = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.cdr.markForCheck();

    this.loginService.login({
      userName: this.loginForm.userName.trim(),
      password: this.loginForm.password,
    }).subscribe({
      next: (res: any) => {
        const token = res?.accessToken;
        if (!token) {
          this.errorMessage = 'Đăng nhập thất bại: Không tìm thấy token.';
          this.submitting = false;
          this.cdr.markForCheck();
          return;
        }

        this.loginService.saveToken(token);
        this.loginService.saveUserCode(this.loginForm.userName.trim());
        this.loginService.saveUserDetail(JSON.stringify(res?.userDetail));

        // Connect SSE once globally after login
        const userDetail = res?.userDetail ?? this.loginService.getUserDetail();
        const userId = Number(userDetail?.id || userDetail?.userId || userDetail?.conversationId || 0);
        if (userId) {
          this.sseService.connect(userId);
        }
        
        console.log('DEBUG: registerSuccess emit', { userDetail });
        this.registerSuccess.emit(true);
        this.loggedIn.emit();
        this.submitting = false;
        this.close();
        this.router.navigate(['/dashboard/stocks']);
      },
      error: (err) => {
        this.errorMessage = err?.error?.message || 'Đăng nhập thất bại. Vui lòng kiểm tra lại tài khoản.';
        this.submitting = false;
        this.cdr.markForCheck();
      }
    });
  }

  submitRegister(): void {
    if (!this.canRegister()) return;

    this.submitting = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.cdr.markForCheck();

    const payload: RegisterRequest = {
      userCode: this.registerForm.userCode.trim(),
      userName: this.registerForm.userName.trim(),
      phoneNumber: this.registerForm.phoneNumber.trim() || undefined,
      password: this.registerForm.password,
      passwordCurrent: '',
      remark: this.registerForm.remark.trim() || undefined,
      birthDay: this.registerForm.birthDay || undefined,
      bankName: this.registerForm.bankName.trim() || undefined,
      bankAccountNumber: this.registerForm.bankAccountNumber.trim() || undefined,
      bankAccountName: this.registerForm.bankAccountName.trim() || undefined,
      bankBranch: this.registerForm.bankBranch.trim() || undefined,
    };

    this.registerService.register(payload).subscribe({
      next: () => {
        this.successMessage = 'Đăng ký thành công. Vui lòng đăng nhập để tiếp tục.';
        this.submitting = false;
        this.loginForm.userName = payload.userCode;
        this.loginForm.password = '';
        this.registerForm.password = '';
        this.registerForm.confirmPassword = '';
        this.activeTab = 'login';
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.errorMessage = err?.error?.message || 'Tên đăng nhập đã tồn tại. Vui lòng thử lại.';
        this.submitting = false;
        this.cdr.markForCheck();
      }
    });
  }
}
